/**
 * TasksPage Component
 *
 * Main entry point for the Tasks section.
 * Fetches real FHIR Task resources from Medplum.
 */

import {
  Alert,
  Box,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Task } from '@medplum/fhirtypes';
import {
  getPractitionerOrganizationRef,
  getRole,
} from '@unify/fhir-model/resources/practitioner';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TaskBulkActions,
  TaskDrawer,
  TaskEmptyState,
  TaskFiltersPanel,
  TaskTableView,
} from './components';
import type { BulkEditChanges } from './components/TaskBulkActions';
import { useTaskFiltering, useTasks, useTeamMembers } from './hooks';
import type { TaskFilters as TaskFiltersType, TaskInboxStatus, TaskPriority } from './types';
import { defaultTaskFilters } from './types';
import {
  deleteTask as deleteTaskService,
  bulkUpdateTasks,
  TaskUpdateError,
  updateTask as updateTaskService,
} from './services/taskService';
import type { BulkUpdateFields } from './services/taskService';
import {
  buildProviderTaskNavigationUrl,
  resolveProviderTaskDestination,
} from '../../lib/providerTaskNavigation';
import { FEATURE_VISIBILITY } from '../../config/featureVisibility';
import { usePermissions } from '../../contexts/PermissionContext';
import { taskInboxCandidatesForViewer, taskInboxOwnerScope } from './taskInboxScope';

export function TasksPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role } = usePermissions();
  const navigate = useNavigate();

  const currentUserRef = useMemo<string | undefined>(() => {
    if (profile?.resourceType === 'Practitioner' && profile.id) {
      return `Practitioner/${profile.id}`;
    }
    return undefined;
  }, [profile]);

  // Real task data from Medplum
  const {
    tasks,
    loading,
    error,
    refresh,
    optimisticUpdate,
    optimisticRemove,
    setTasks,
  } = useTasks();

  // Team members for assignee selectors
  const { members: teamMembers } = useTeamMembers();
  const viewerOrganizationId = useMemo(
    () => (profile?.resourceType === 'Practitioner'
      ? getPractitionerOrganizationRef(profile)?.reference?.replace(/^Organization\//, '')
      : undefined),
    [profile],
  );
  const viewerRole = useMemo(
    () => (profile?.resourceType === 'Practitioner'
      ? getRole(profile) ?? (role === 'client' ? undefined : role)
      : undefined),
    [profile, role],
  );
  const visibleMembers = useMemo(() => {
    const allowed = new Set(taskInboxCandidatesForViewer({
      viewerRole,
      viewerId: profile?.id,
      viewerOrganizationId,
      candidates: teamMembers,
    }).map((member) => member.reference));
    return teamMembers.filter((member) => allowed.has(member.reference));
  }, [profile, viewerRole, viewerOrganizationId, teamMembers]);
  const { allowedOwnerRefs, anyOrganizationOwner } = useMemo(
    () => taskInboxOwnerScope({
      viewerRole,
      viewerOrganizationId,
      practitionerRefs: visibleMembers.map((member) => member.reference),
    }),
    [viewerRole, viewerOrganizationId, visibleMembers],
  );
  const filterMembers = useMemo(
    () => visibleMembers.filter((member) => member.reference !== currentUserRef),
    [visibleMembers, currentUserRef],
  );

  // UI state
  const [filters, setFilters] = useState<TaskFiltersType>(defaultTaskFilters);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());

  // Task modal state
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'create'>('view');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Delete confirmation modal
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [bulkDeleteConfirmOpened, { open: openBulkDeleteConfirm, close: closeBulkDeleteConfirm }] = useDisclosure(false);

  // Filter and compute task data
  const { filteredTasks, overdueCount } = useTaskFiltering(
    tasks,
    filters,
    currentUserRef,
    allowedOwnerRefs,
    anyOrganizationOwner,
  );

  const displayTasks = useMemo(() => {
    const byRef = new Map(teamMembers.map((m) => [m.reference, m.name]));
    return filteredTasks.map((t) => {
      const name = t.owner?.reference ? byRef.get(t.owner.reference) : undefined;
      if (!name) return t;
      return { ...t, owner: { ...t.owner, display: name } };
    });
  }, [filteredTasks, teamMembers]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleNavigateToTask = useCallback(
    async (task: Task): Promise<void> => {
      const destination = await resolveProviderTaskDestination(medplum, task);
      if (destination) {
        navigate(buildProviderTaskNavigationUrl(destination));
        return;
      }
      setSelectedTask(task);
      setModalMode('view');
      openModal();
    },
    [medplum, navigate, openModal],
  );

  const handleDeleteTask = useCallback(
    (task: Task): void => {
      setTaskToDelete(task);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const confirmDeleteTask = useCallback(async (): Promise<void> => {
    if (!taskToDelete) return;
    const snapshot = tasks;
    optimisticRemove(taskToDelete.id!);
    setSelectedTasks((prev) => {
      const next = new Set(prev);
      next.delete(taskToDelete.id!);
      return next;
    });
    closeDeleteModal();
    setTaskToDelete(null);

    try {
      await deleteTaskService(medplum, taskToDelete);
      showNotification({ color: 'green', message: 'Task deleted successfully' });
    } catch (err) {
      setTasks(snapshot);
      const msg = isPermissionError(err)
        ? 'You do not have permission to delete this task'
        : 'Failed to delete task';
      showNotification({ color: 'red', message: msg });
    }
  }, [taskToDelete, tasks, medplum, optimisticRemove, setTasks, closeDeleteModal]);

  const handleSaveTask = useCallback(
    async (savedTask: Task): Promise<void> => {
      const original = tasks.find((t) => t.id === savedTask.id);
      if (!original) return;
      optimisticUpdate(savedTask);
      try {
        const updated = await updateTaskService(medplum, original, {
          description: savedTask.description || '',
          status: savedTask.status as TaskInboxStatus,
          priority: (savedTask.priority as TaskPriority) || 'routine',
          dueDate: savedTask.restriction?.period?.end || null,
          assignee: savedTask.owner ? { reference: savedTask.owner.reference, display: savedTask.owner.display } : null,
          patient: savedTask.for ? { reference: savedTask.for.reference, display: savedTask.for.display } : null,
          notes: savedTask.note?.[0]?.text || '',
        });
        optimisticUpdate(updated);
        showNotification({ color: 'green', message: 'Task updated successfully' });
      } catch (err) {
        // A failed multi-step update may have applied earlier steps; show the server's copy, not the stale original.
        const serverTask = err instanceof TaskUpdateError ? err.serverTask : undefined;
        optimisticUpdate(serverTask ?? original);
        if (!serverTask) refresh();
        const msg = isPermissionError(err instanceof TaskUpdateError ? err.cause : err)
          ? 'You do not have permission to update this task'
          : 'Failed to update task';
        showNotification({ color: 'red', message: msg });
      }
    },
    [medplum, tasks, optimisticUpdate, refresh],
  );

  const handleBulkApplyChanges = useCallback(
    async (changes: BulkEditChanges): Promise<void> => {
      const selectedTaskList = tasks.filter((t) => selectedTasks.has(t.id!));
      if (selectedTaskList.length === 0) return;

      const snapshot = [...tasks];

      // Build bulk update fields
      const bulkFields: BulkUpdateFields = {};
      if (changes.status != null) bulkFields.status = changes.status;
      if (changes.priority != null) bulkFields.priority = changes.priority;
      if (changes.assignee != null) {
        const member = teamMembers.find((m) => m.reference === changes.assignee);
        if (member) {
          bulkFields.assignee = {
            reference: member.reference,
            ...(member.preferredName ? { display: member.preferredName } : {}),
          };
        }
      }

      // Optimistic update
      setTasks((prev) =>
        prev.map((t) => {
          if (!selectedTasks.has(t.id!)) return t;
          const updated = { ...t };
          if (bulkFields.status != null) updated.status = bulkFields.status;
          if (bulkFields.priority != null) updated.priority = bulkFields.priority;
          if (bulkFields.assignee != null) {
            updated.owner = { reference: bulkFields.assignee.reference, display: bulkFields.assignee.display };
          }
          return updated;
        }),
      );
      setSelectedTasks(new Set());

      try {
        const result = await bulkUpdateTasks(medplum, selectedTaskList, bulkFields);
        if (result.failed.length > 0) {
          setTasks(snapshot);
          refresh();
          showNotification({
            color: 'yellow',
            message: `${result.succeeded.length} task(s) updated, ${result.failed.length} failed. Refreshing...`,
            autoClose: 5000,
          });
        } else {
          showNotification({
            color: 'green',
            message: `${result.succeeded.length} task(s) updated successfully`,
          });
        }
      } catch {
        setTasks(snapshot);
        showNotification({ color: 'red', message: 'Failed to update tasks' });
      }
    },
    [tasks, selectedTasks, teamMembers, medplum, setTasks, refresh],
  );

  const handleBulkDelete = useCallback((): void => {
    openBulkDeleteConfirm();
  }, [openBulkDeleteConfirm]);

  const confirmBulkDelete = useCallback(async (): Promise<void> => {
    const selectedTaskList = tasks.filter((t) => selectedTasks.has(t.id!));
    const snapshot = [...tasks];

    setTasks((prev) => prev.filter((t) => !selectedTasks.has(t.id!)));
    setSelectedTasks(new Set());
    closeBulkDeleteConfirm();

    try {
      const results = await Promise.allSettled(
        selectedTaskList.map((t) => deleteTaskService(medplum, t)),
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        setTasks(snapshot);
        refresh();
        showNotification({
          color: 'yellow',
          message: `${results.length - failures.length} task(s) deleted, ${failures.length} failed. Refreshing...`,
        });
      } else {
        showNotification({
          color: 'green',
          message: `${selectedTaskList.length} task(s) deleted successfully`,
        });
      }
    } catch {
      setTasks(snapshot);
      showNotification({ color: 'red', message: 'Failed to delete tasks' });
    }
  }, [selectedTasks, tasks, medplum, setTasks, refresh, closeBulkDeleteConfirm]);

  const handleClearSelection = useCallback((): void => {
    setSelectedTasks(new Set());
  }, []);

  const handleClearFilters = useCallback((): void => {
    setFilters({
      ...filters,
      provider: defaultTaskFilters.provider,
      taskType: defaultTaskFilters.taskType,
      status: [],
      assignee: 'all',
      search: '',
      showOverdueOnly: false,
    });
  }, [filters]);

  const hasFilters =
    filters.status.length > 0 ||
    filters.assignee !== 'all' ||
    filters.search !== '' ||
    filters.showOverdueOnly ||
    filters.provider.length !== defaultTaskFilters.provider.length ||
    filters.provider.some((p, i) => p !== defaultTaskFilters.provider[i]) ||
    filters.taskType.length > 0;

  const showEmptyState = !loading && !error && filteredTasks.length === 0;
  const emptyStateVariant = loading
    ? 'loading'
    : error
      ? 'error'
      : hasFilters
        ? 'no-results'
        : 'no-tasks';

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Tasks</Title>
            <Text c="dimmed" size="sm">
              Manage your clinical and administrative tasks
            </Text>
          </div>
        </Group>

        <TaskFiltersPanel
          filters={filters}
          onFiltersChange={setFilters}
          overdueCount={overdueCount}
          teamMembers={filterMembers}
        />

        <Paper p="lg" withBorder>
          {loading || error || showEmptyState ? (
            <TaskEmptyState
              variant={emptyStateVariant}
              hasFilters={hasFilters}
              onClearFilters={handleClearFilters}
              onRetry={refresh}
              errorMessage={error ?? undefined}
            />
          ) : (
            <TaskTableView
              tasks={displayTasks}
              onTaskClick={handleNavigateToTask}
              sortBy={filters.sortBy}
              sortDirection={filters.sortDirection}
              showAssignee
              onSort={(column) => {
                setFilters((prev) =>
                  prev.sortBy === column
                    ? { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' }
                    : { ...prev, sortBy: column, sortDirection: 'asc' },
                );
              }}
            />
          )}
        </Paper>

        {/* Bulk Actions Bar */}
        {FEATURE_VISIBILITY.tasksAdvanced ? (
        <TaskBulkActions
          selectedCount={selectedTasks.size}
          onClearSelection={handleClearSelection}
          onBulkApplyChanges={handleBulkApplyChanges}
          onBulkDelete={handleBulkDelete}
          teamMembers={visibleMembers}
        />
        ) : null}

        {/* Task Modal */}
        <TaskDrawer
          opened={modalOpened}
          onClose={closeModal}
          task={selectedTask}
          mode={modalMode}
          onSave={handleSaveTask}
          onDelete={handleDeleteTask}
          teamMembers={visibleMembers}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Task"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Confirm Deletion"
              color="red"
              variant="light"
            >
              Are you sure you want to delete this task? This action cannot be undone.
            </Alert>
            {taskToDelete && (
              <Box p="sm" bg="gray.0" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                <Text size="sm" fw={500}>
                  {taskToDelete.description}
                </Text>
              </Box>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmDeleteTask}>
                Delete Task
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Bulk Delete Confirmation Modal */}
        <Modal
          opened={bulkDeleteConfirmOpened}
          onClose={closeBulkDeleteConfirm}
          title="Delete Multiple Tasks"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Confirm Bulk Deletion"
              color="red"
              variant="light"
            >
              Are you sure you want to delete {selectedTasks.size} task(s)? This action cannot be
              undone.
            </Alert>
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeBulkDeleteConfirm}>
                Cancel
              </Button>
              <Button color="red" onClick={confirmBulkDelete}>
                Delete {selectedTasks.size} Task(s)
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function isPermissionError(err: unknown): boolean {
  if (err && typeof err === 'object' && 'status' in err) {
    return (err as { status: number }).status === 403;
  }
  return false;
}
